// Parser for `.hw` chromatography data files written by HW-2000 / N3800 /
// Qianpu (千谱) Chinese chromatography workstations (vendor: 北京分析仪器厂).
//
// ---------------------------------------------------------------------------
// SCOPE: This file is the SINGLE SOURCE OF TRUTH for everything about
// the .hw on-disk format. No other module in the tree is allowed to:
//   • know byte offsets / widths
//   • re-derive values from the raw `Signal` / `Header` / `G[]` slots
//   • own a display-format choice that mirrors HW.exe's output
//
// Downstream consumers (UI, tests, `re/` probes, `verify_real_world.ts`)
// import named accessors and HW.exe-faithful formatters from here and
// nothing else. If a fact about the format needs to change, it changes
// in this file and propagates.
//
// ---------------------------------------------------------------------------
// FORMAT SUMMARY (verified by disassembling HWComp.exe sub_402A20 +
// HWgram.exe sub_403740, cross-checked against running HW.exe on the
// real-world fixtures under `testdata/`):
//
//   offset 0..12   : ASCII magic "HW Data Fi0X\n" / "HW Data File\n"
//   offset 13..16  : uint32 LE — sample count N
//   offset 17..    : N × int32 LE — raw detector counts
//   then           : Header        — 26 × uint32 = 104 bytes
//   then           : ReportText1   — MFC CString (GBK), report header
//   then           : ReportText2   — MFC CString (GBK), report footer
//   then           : Components[]  — calibration / method rows  (U11 entries)
//   then           : MidRecords[]  — integration-parameter rows (U13 entries)
//   then           : Peaks[]       — measured-peak rows         (U12 entries)
//   then (Fi02..05): TailRecords[] — 96-byte structured-tail records (U19 entries)
//   then (Fi02..05): TailPad       — 20 B (Fi02/03) or 28 B (Fi04/05)
//   then (Fi02..05): AuditLog (MFC CString); GBK timestamps on Fi04/05,
//                    opaque binary on Fi02/03
//   then (Fi02..05): TrailingU32 (last 4 bytes; meaning unknown)
//
// Each Component / Peak row has identical layout: 4 (StartSamples) +
// N1 (name) + 5 × 4 (G[]) + N2 (flag) bytes, where (N1, N2) is
// (17, 7) on Fi00, (33, 7) on Fi01/02, and (33, 33) on Fi03/04/05.
//
// ---------------------------------------------------------------------------
// CONFIDENCE TIERS (per public field; printed in the JSDoc):
//
//   [Tier 3]  PROVEN — byte-type AND semantic meaning are confirmed by
//             BOTH (a) disassembly of HWgram.exe / HWComp.exe (see
//             `re/ida_hwgram` and `re/ida_hwcomp`) AND (b) at least one
//             value-level cross-check against HW.exe's own output on a
//             real-world fixture — either the data-view shown to the
//             operator, or the RTF / PNG report HW.exe writes when
//             "Export" is invoked, or the chromatogram pixel mapping.
//             The verifying probe lives under `re/` and is referenced
//             by name in the field's doc.
//
//             Promotion rule: nothing reaches Tier 3 on disassembly alone.
//             A field stays at Tier 2 until at least one HW.exe-output
//             comparison passes for it.
//
//   [Tier 2]  TYPE-CONFIRMED — the binary type and byte position are
//             known (we read the right slice in the right encoding) and
//             at least one viewer reads the bytes downstream, but the
//             semantic meaning is inferred from disassembly context only;
//             we have no HW.exe-displayed value to compare against.
//
//   [Tier 1]  SPECULATIVE — guessed name and/or interpretation, no
//             confirmed disassembly reference and no display oracle. The
//             bytes are what's on disk; what they mean is speculative.
//
// Naming convention for Tier 1/2 header dwords: `U<N>` where N is the
// uint32 slot index 0..25 — so consumers can map straight to the
// disassembly. Tier 3 header dwords get semantic names. The previous
// `Unk17`/`UnkNN` mixed scheme has been retired.

/** [Tier 3] Length in bytes of every known HW magic string ("HW Data XXX\n"). */
export const MAGIC_LEN = 13;

interface KnownMagic {
  bytes: string;
  version: string;
}

/** [Tier 3] All six file-format magics observed in the wild and the
 *  two-character version code each one maps to. The trailing `\n` is
 *  part of the magic, not a line separator. Cross-checked against
 *  HWgram.exe sub_403740 lines 1915–1941 (six successive `lstrcmpA`s). */
const KNOWN_MAGICS: KnownMagic[] = [
  { bytes: "HW Data File\n", version: "00" },
  { bytes: "HW Data Fi01\n", version: "01" },
  { bytes: "HW Data Fi02\n", version: "02" },
  { bytes: "HW Data Fi03\n", version: "03" },
  { bytes: "HW Data Fi04\n", version: "04" },
  { bytes: "HW Data Fi05\n", version: "05" },
];

/** Default sampling rate. Matches the value HWgram.exe / HWComp.exe
 *  both use unconditionally: they read `n20` from a side-car file
 *  `HWFrequence.txt` in the install directory, defaulting to **20** when
 *  the file is absent or unreadable. The shipped `HWFrequence.txt` reads
 *  `20,20` (two values), but only the first is parsed via `fscanf("%d")`
 *  — the second is currently dead config (verified in HWgram and HWComp).
 *
 *  IMPORTANT: every .hw file in the wild stores `Header.U6 = 200000`
 *  (= 5 Hz, the value the prior Go port called "TickUS"), but the
 *  official software *ignores* that field — it is a stale placeholder
 *  the writer never updates. All retention-time, area, and height
 *  conversions in HWComp use `n20` from `HWFrequence.txt`, not the
 *  on-disk slot 6. We therefore default to 20 Hz to match the vendor's
 *  chromatograms exactly. Callers with a customized `HWFrequence.txt`
 *  can pass `parse(buf, { samplingHz: N })` or
 *  `parse(buf, { useHeaderTickUS: true })` to opt into the legacy
 *  slot-6-derived behavior. */
export const DEFAULT_SAMPLING_HZ = 20.0;

/** 26-uint32 fixed-layout block immediately following the signal trace.
 *  All fields are little-endian unsigned 32-bit unless noted otherwise.
 *  Confidence tiers are per individual field; see the file header for
 *  the tier definitions. Tier 1/2 dwords use raw `U<N>` names so they
 *  map straight to slot indices in the disassembly. */
export interface Header {
  /** [Tier 1] Slot 0 — purpose unknown. Always 0 in the corpus.
   *  Read by HWComp (sub_402A20 `v144 = h[0]`) and discarded. */
  U0: number;
  /** [Tier 1] Slot 1 — purpose unknown. 0 in most files; 1 in PONA, 2
   *  in baijiu, 4 in byqyou. Possibly a channel/detector index or
   *  format-variant tag. Read by HWComp into `v129` and discarded. */
  U1: number;
  /** [Tier 1] Slot 2 — purpose unknown. Usually 1. Read into `v130` and
   *  discarded. */
  U2: number;
  /** [Tier 1] Slot 3 — purpose unknown. Always 0 in the corpus. */
  U3: number;
  /** [Tier 1] Slot 4 — purpose unknown. Often 40 (modern), 199 in PONA,
   *  18/34/43 in older demos. */
  U4: number;
  /** [Tier 1] Slot 5 — purpose unknown. Usually 5; 159 in ningjiao demo. */
  U5: number;
  /** [Tier 2] Slot 6 — would *plausibly* be the per-sample tick in
   *  microseconds (200000 ⇒ 5 Hz); the prior Go port labelled it
   *  `TickUS` accordingly. **HW.exe / HWgram / HWComp all ignore this
   *  field** and read sampling rate from `HWFrequence.txt` instead.
   *  Every production .hw file stores 200000 regardless of the actual
   *  capture rate. Surfaced here for diagnostic display only — do not
   *  use to derive `SamplingHz` unless `parse()` is called with
   *  `{ useHeaderTickUS: true }`. */
  U6: number;
  /** [Tier 1] Slot 7 — purpose unknown. Always 1 in the corpus. */
  U7: number;
  /** [Tier 1] Slot 8 — purpose unknown. 0 / 8 / 10 / 35469032 — wildly
   *  variable, never used by either viewer. */
  U8: number;
  /** [Tier 1] Slot 9 — purpose unknown. Always 0 in the corpus. */
  U9: number;
  /** [Tier 1] Slot 10 — purpose unknown. Always 0 in the corpus. */
  U10: number;
  /** [Tier 3] Slot 11 — number of calibration / method records that
   *  follow the two report CStrings. Drives the `Components[]` loop
   *  length. Disassembly: HWgram sub_403740 line 2015. Cross-check:
   *  every fixture's `Components.length === Header.PreCount` (parser
   *  test "parses *.hw" walks the whole corpus). */
  PreCount: number;
  /** [Tier 3] Slot 12 — number of measured peak records. Drives the
   *  `Peaks[]` loop length. Disassembly: HWgram sub_403740 lines
   *  2272–2275. Cross-check: peak counts on every Fi02..Fi05 fixture
   *  match HW.exe's RTF row count to the row (`re/verify_real_world.ts`). */
  PeakCount: number;
  /** [Tier 3] Slot 13 — number of mid-block 16-byte records that appear
   *  between Components and Peaks. Drives the `MidRecords[]` loop length.
   *  Disassembly: HWgram sub_403740 line 2139 + the subsequent 16-byte
   *  read loop. Cross-check: parsing succeeds (no truncation) on every
   *  fixture in the corpus. */
  MidCount: number;
  /** [Tier 1] Slot 14 — purpose unknown. Often 5. */
  U14: number;
  /** [Tier 3] Slot 15 — Unix epoch (seconds since 1970-01-01 UTC) of
   *  the chromatogram acquisition. Disassembly: HWComp sub_402A20 line
   *  1985 stores into `*((_DWORD *)this + 33165)`, then sub_404D80
   *  formats via `CTime::GetLocalTm` + `"%Y.%m.%d %H:%M:%S"`.
   *  Cross-check: `re/verify_real_world.ts` matches this against the
   *  "进样时间" line HW.exe writes into the RTF preamble for every
   *  real-world fixture. Convenience accessor: {@link HwFile.AcquisitionTime}. */
  AcquisitionTimestamp: number;
  /** [Tier 1] Slot 16 — purpose unknown. Often 5. Read by HWComp into
   *  a local (`v140`) and discarded (sub_402A20 line 1548-1552). */
  U16: number;
  /** [Tier 1] Slot 17 — purpose unknown. Typical value 1000. The prior
   *  Go port labelled this "MinPeakArea" but HWComp reads it into a
   *  local (`v143`) and never uses it (sub_402A20 line 1561). */
  U17: number;
  /** [Tier 1] Slot 18 — purpose unknown. Always 33 in modern files, 63
   *  in PONA. The prior Go port labelled this "Name1Width" but neither
   *  viewer uses this field for record sizing — both derive name widths
   *  from the version code via {@link recordWidths}. */
  U18: number;
  /** [Tier 3] Slot 19 — number of 96-byte detection / integration event
   *  records that lead the structured tail (after `Peaks[]`). Drives
   *  the `TailRecords[]` loop length. Cross-check: exhaustive layout
   *  validation across the 14-file Fi02..Fi05 corpus
   *  (`re/probe_layout_all.ts`, `re/check_invariants.ts`) — every file's
   *  tail begins with exactly `U19 × 96` bytes that decode as a valid
   *  event stream with the expected per-field invariants. */
  U19: number;
  /** [Tier 2] Slot 20 — maximum acquisition length in *samples* at the
   *  default 20 Hz rate. Always exactly divisible by 20 across the
   *  corpus and matches each file's instrument run-time cap:
   *    1 728 000 / 20 = 86 400 s = 24 h  (modern files)
   *      276 000 / 20 = 13 800 s ≈ 230 min  (PONA, the longest demo)
   *       18 000 / 20 =    900 s = 15 min  (byqyou demo)
   *       24 000 / 20 =  1 200 s = 20 min  (ningjiao demo)
   *  Likely the per-method "max acquisition window" the writer reserves
   *  buffer for. HWComp reads then discards it (`v149`, sub_402A20
   *  line 1631). No HW.exe display to verify against → Tier 2. */
  U20: number;
  /** [Tier 1] Slot 21 — purpose unknown. Always 1 in the corpus. HWComp
   *  reads into `v150` and discards (sub_402A20 line 1640). */
  U21: number;
  /** [Tier 1] Slot 22 — float32 on disk. Highly variable across files
   *  (1.0 / 1.307 / 0.619 / 55.56 / 16.67 — clearly meaningful), but
   *  HWComp reads it as `_DWORD` (sub_402A20 line 1611, `v147`) and
   *  never uses it. The prior Go port called this "Scale1" and
   *  multiplied peak widths by it; that formula is **unsupported by
   *  the disassembly** (HWComp's width column is `g[4] / n20`, not
   *  `g[4] * Scale1`). Type is float32 on disk (bit pattern matches; a
   *  uint32 reading would yield 1065353216 for the typical 1.0 case). */
  U22: number;
  /** [Tier 3] Slot 23 — float32, per-channel **Amount** scaling factor
   *  (sample injection amount). Disassembly: HWComp sub_402A20 line
   *  1647 stores at `*(this + n2 + 32153)`, then sub_405210 case 4
   *  prints in the report's "Amnt" column with `%-7.4g` (line 3998).
   *  Cross-check: pinned values 101.3 (byqyou Fi02 demo) — see test
   *  "Amount and Dilution decode as plausible floats". */
  Amount: number;
  /** [Tier 1] Slot 24 — float32 on disk. Always 1.0 across the corpus.
   *  HWComp reads as `_DWORD` (sub_402A20 line 1631, `v149`) and
   *  discards. */
  U24: number;
  /** [Tier 3] Slot 25 — float32, per-channel **Dilution** factor.
   *  Disassembly: HWComp sub_402A20 line 1648 stores at
   *  `*(this + n2 + 32653)`, then sub_405210 case 5 prints in the
   *  report's "Dilu" column with `%-7.4g` (line 4020). Cross-check:
   *  pinned value 11.7632 (byqyou Fi02 demo). */
  Dilution: number;
}

const HEADER_SIZE = 26 * 4;

/** One row of the calibration / method table OR the measured peak table.
 *
 *  Both tables share the **identical** on-disk layout (4 + N1 + 20 + N2
 *  bytes, all little-endian) — they're stored back-to-back and HW.exe
 *  treats Components as the calibration template and Peaks as that
 *  template's instantiation for the current run. We model them as one
 *  type and alias `Component`/`Peak` to it for grep clarity at use
 *  sites.
 *
 *  Layout:
 *    bytes 0..4         : float32 retention-time anchor (in samples)
 *    bytes 4..(4+N1)    : GBK component name, NUL/space padded
 *    bytes (4+N1)..+20  : 5 × 4-byte slots — see {@link PeakRow.G}
 *    bytes (4+N1+20)..  : GBK flag string, NUL/space padded (length N2)
 *
 *  N1 / N2 are version-dependent — see {@link recordWidths}.
 *
 *  Only the on-disk fields live here. Everything HW.exe *displays* is
 *  derived via the named accessors below ({@link peakRetentionMin},
 *  {@link peakAreaCounts}, {@link peakHeightCounts}, {@link peakFwhmSec},
 *  {@link peakAreaPercent}). The accessors are the **single source of
 *  truth** for the formulas HW.exe uses; the UI, tests, and probes all
 *  call them — they never re-derive from `G[]` directly. */
export interface PeakRow {
  /** Zero-based index in the parent array (Components or Peaks).
   *  Array bookkeeping, not decoded from disk. */
  Index: number;
  /** [Tier 3] Decoded GBK name. Trimmed of NUL and trailing spaces.
   *  Empty for unnamed peaks; non-empty when HW.exe matched the row
   *  against a calibration entry (e.g. baijiu demo's "乙醛", "乙酸乙酯"). */
  Name: string;
  /** [Tier 3] Decoded GBK peak-integration flag string. HW.exe labels
   *  this column **峰标志** in its data view — not a free-form note,
   *  despite the field width. Empirically (`re/probe_note_values.ts`)
   *  the value is a short concatenation of single-letter integration
   *  codes:
   *      L  = left-baseline event (peak start by tangent skim)
   *      R  = right-baseline event (peak end by tangent skim)
   *      M  = manual integration override
   *      V  = valley-to-valley integration
   *      T  = tailing-peak handling
   *  combined into multi-letter strings (e.g. "LRMV", "RM", "VT"); empty
   *  string for ordinary peaks. The slot is also wide enough that
   *  operators occasionally type ad-hoc annotations there ("missed",
   *  "1 peak", "35peaks" all observed in `demo_PONA_Fi04.hw`). */
  Flag: string;
  /** [Tier 3] Float32 from disk: peak retention-time anchor in **sample
   *  units** (NOT minutes — divide by SamplingHz × 60 for minutes;
   *  use {@link peakRetentionMin}). Disassembly: float32 at offset 0 of
   *  the record. Cross-check: feeds into the verified retention-min
   *  derivation. */
  StartSamples: number;
  /** [Tier 2] The five 4-byte slots that follow the name field. Stored
   *  here as a homogenous-typed tuple of `number`, but the on-disk types
   *  are mixed:
   *      G[0] = float32  — area% (set by HW.exe at save time; see
   *                         {@link peakAreaPercent})
   *      G[1] = float32  — semantics unknown (Tier 1; empirically zero
   *                         on every uncalibrated run)
   *      G[2] = float32  — area in counts·samples (divide by SamplingHz
   *                         for HW.exe's "峰面积" column; see
   *                         {@link peakAreaCounts})
   *      G[3] = int32    — height in raw detector counts; see
   *                         {@link peakHeightCounts}
   *      G[4] = float32  — FWHM in samples (divide by SamplingHz for
   *                         HW.exe's "半高峰宽" column; see
   *                         {@link peakFwhmSec})
   *
   *  Every consumer should call the named accessors instead of indexing
   *  `G[]` directly. The raw tuple is exposed only for diagnostic /
   *  reverse-engineering use. */
  G: [number, number, number, number, number];
  /** [Tier 3] Defensive copy of the raw on-disk record bytes, useful
   *  for diagnostics and re-decoding under different assumptions. */
  Raw: Uint8Array;
}

/** Alias for {@link PeakRow} used in the calibration / method table.
 *  Same on-disk layout as `Peak`; the distinction is purely positional. */
export type Component = PeakRow;
/** Alias for {@link PeakRow} used in the measured-peak table. */
export type Peak = PeakRow;

/** One row of the U13 mid-block. 16 bytes per record, sandwiched between
 *  the Components[] and Peaks[] arrays. The block exists in every
 *  Fi02..Fi05 file in the corpus and HWgram correctly skips
 *  `Header.MidCount × 16` bytes here, so the *framing* is Tier 3. The
 *  *contents* are not interpreted by either viewer and the field names
 *  below are intentionally generic byte-level labels. */
export interface MidRecord {
  /** [Tier 2] First 4 bytes of the record, read as little-endian uint32.
   *  HWgram sub_403740 lines 2154–2169 confirm 4 bytes are consumed here
   *  but never reads the value back. Empirically often zero or a small
   *  index — semantics unconfirmed; no HW.exe display compares against
   *  this slot. */
  Word0: number;
  /** [Tier 2] Bytes 4..16, read as three little-endian float32s. The
   *  float32 reading is the most plausible interpretation given the
   *  surrounding structure (peak-table neighbours store float32s at
   *  the same offsets), but neither viewer reads these and no HW.exe
   *  display oracle exists to confirm. */
  Floats123: [number, number, number];
}

/** One 96-byte record from the structured tail.
 *
 *  The tail of every Fi02..Fi05 file (after `Peaks[]`) starts with
 *  exactly `Header.U19` consecutive 96-byte records. Each record holds
 *  24 little-endian 32-bit words. The framing — count, size, position —
 *  is Tier 3 (verified by exhaustive layout validation across the
 *  14-file corpus, `re/probe_layout_all.ts`).
 *
 *  Per-word *meaning* is **not** Tier 3: HW.exe was not decompiled in
 *  detail for the writer side, and HWgram/HWComp do not display these
 *  records anywhere we can compare against. Field statistics observed
 *  empirically across the corpus (`re/probe_rec_stats.ts`,
 *  `re/check_invariants.ts`):
 *
 *    - **Word 0 and word 1 are BOTH monotonically non-decreasing across
 *      records** (verified on every file). They look like two
 *      independent sample-index cursors that an integration engine
 *      might walk forward together — not a start/end pair, since word 1
 *      is occasionally smaller than word 0 within the same record
 *      (~3-5 % of records). Same independent-monotonic pattern holds
 *      for word pairs (4,5), (8,9), (12,13).
 *    - Words 2/3, 6/7 carry large counters that look like
 *      detector-count sums.
 *    - Word 10 is a small enum (0..6 in UIO.hw); word 11 reads as a
 *      sensible float32 (e.g. 84.5 / 100.0 / 321.5); words 14/15 look
 *      like baseline detector-count snapshots.
 *    - Words 16/17/22 are constant `1` in every record we've seen;
 *      words 18..21 are 0/1/2 flags; word 23 is constant `0`.
 *
 *  All of the above is *suggestive* of integration / detection events,
 *  but until either the HW.exe writer is decompiled or HW.exe exposes
 *  these values in some UI we can read, we expose the records under a
 *  byte-level name (`TailRecord96`) with the highest-confidence
 *  per-word interpretations surfaced via accessor naming that does NOT
 *  imply meaning. */
export interface TailRecord96 {
  /** Zero-based index into `HwFile.TailRecords`. Array bookkeeping,
   *  not decoded from disk. */
  Index: number;
  /** [Tier 2] Word 0 — uint32, monotonically non-decreasing across the
   *  record stream. Magnitude consistent with a sample-index cursor
   *  into the signal trace, but unverified. */
  Word0: number;
  /** [Tier 2] Word 1 — uint32, also independently monotonically
   *  non-decreasing. See {@link Word0}. */
  Word1: number;
  /** [Tier 2] Word 10 — uint32, observed range 0..~10 across the
   *  corpus. Looks like a small enum; meaning unconfirmed. */
  Word10: number;
  /** [Tier 2] Word 11 reinterpreted as little-endian float32. Always
   *  reads as a sensible finite number (e.g. 84.5, 100.0, 321.5);
   *  semantics unconfirmed. */
  Word11AsFloat: number;
  /** [Tier 2] All 24 little-endian uint32 words exactly as they appear
   *  on disk. Use a DataView on `Raw` if you need to reinterpret a word
   *  as float32 / int32 / two int16s. */
  Words: Uint32Array;
  /** [Tier 2] Raw 96 bytes for opaque consumers / round-tripping. */
  Raw: Uint8Array;
}

/** @deprecated Renamed to {@link TailRecord96}. The old "DetectionEvent"
 *  name asserted a meaning ("integration event") that is not Tier 3 —
 *  no HW.exe display oracle exists for these records. Provided as a
 *  type alias only. */
export type DetectionEvent = TailRecord96;

/** The fixed pad block that sits between the `DetectionEvent[]` array
 *  and the audit-log MFC CString. Size is version-dependent:
 *
 *    Fi02 / Fi03 → 20 bytes
 *    Fi04 / Fi05 → 28 bytes
 *
 *  The block is overwhelmingly zero across the corpus, with a handful
 *  of non-zero scratch words at known offsets (Fi05 UIO/新煤油 store
 *  `[7,0,-1,0,0,0,0]`, baijiu stores `[-1,0,0,0,46,0,0]`, etc). All
 *  fields Tier 2 — bytes are read correctly and the framing is
 *  validated across the whole corpus, but no individual word's meaning
 *  has been confirmed against any HW.exe display. */
export interface TailPadBlock {
  /** [Tier 2] Padding length in bytes (20 for Fi02/Fi03, 28 for Fi04/Fi05).
   *  Pinned per-version in {@link tailPadSize}; verified across the
   *  full 14-file corpus. */
  Size: number;
  /** [Tier 2] Padding decoded as little-endian uint32 words.
   *  `Words.length === Size / 4`. */
  Words: number[];
  /** [Tier 2] Raw padding bytes. */
  Raw: Uint8Array;
}

/** One line of the appended audit-trail text block. */
export interface AuditEntry {
  /** [Tier 3] Parsed from "YYYY/MM/DD HH:MM:SS" prefix in the GBK
   *  audit-log text. Constructed in **local time** (the timezone of the
   *  workstation that produced the file is not recorded; we assume the
   *  viewer's local timezone matches). */
  Time: Date;
  /** [Tier 3] GBK-decoded message body following the timestamp, trimmed
   *  of leading whitespace and terminating CR/LF/NUL. */
  Message: string;
}

/** One contiguous, named byte-range of the input file, recorded in
 *  on-disk order as the parser walks the format. This is the **single
 *  source of truth** for "where does each section live in the file" —
 *  the UI, debug tools, and probes consume `HwFile.Sections` and never
 *  re-derive offsets themselves.
 *
 *  Sections are emitted **in strict file-byte order**, are
 *  non-overlapping, and (modulo gap sections) cover the whole file.
 *  When the parser cannot decode a region but bytes exist, a section
 *  with `kind: "gap"` is emitted so the unaccounted-for bytes are still
 *  visible. The list is exhaustive enough that
 *  `Σ section.length === file.length` always holds. */
export interface FileSection {
  /** Stable machine-readable identifier (e.g. `"signal"`, `"header"`,
   *  `"components"`, `"tail-records"`, `"audit-log"`, `"trailing-u32"`,
   *  `"gap"`). UI labels are derived from this via i18n. */
  Id: string;
  /** Short human-readable label, English fallback (e.g. `"Signal trace"`,
   *  `"Header (26 × uint32)"`). Translated to the user's locale by the UI. */
  Label: string;
  /** Absolute byte offset from the start of the file. */
  Offset: number;
  /** Length in bytes. `Offset + Length` equals the next section's
   *  `Offset` (or the EOF for the last section). */
  Length: number;
  /** Defensive view of the bytes in this section. Same backing buffer
   *  as the input — do not mutate. */
  Raw: Uint8Array;
  /** Confidence tier of the section's *framing* (the offset/length).
   *  Per-byte semantic tiers live on the typed accessors elsewhere
   *  (`Header.U17` etc.). `1` for `"gap"` and other speculative
   *  partitions, `2` for empirically-validated framings (e.g. tail
   *  records / pad), `3` for disassembly-confirmed framings. */
  Tier: 1 | 2 | 3;
  /** Optional one-line note for the UI tooltip — typically points at
   *  the disassembly reference or the verifying probe. */
  Note?: string;
}

/** Fully decoded representation of a .hw file. */
export interface HwFile {
  /** [Tier 3] Pretty-printed magic string with the trailing `\n`
   *  stripped — one of "HW Data File", "HW Data Fi01" .. "HW Data Fi05". */
  Magic: string;
  /** [Tier 3] Two-character version code "00".."05" derived from the
   *  matched magic. */
  Version: string;
  /** [Tier 3] Byte offset at which the magic was found. The official
   *  loader performs a sliding-window scan with no upper bound; we
   *  mirror that. For all production files seen this is 0. */
  MagicAt: number;

  /** [Tier 3] Number of int32 detector samples that follow the magic.
   *  Read directly from the 4 bytes after the magic. */
  SampleCount: number;
  /** [Tier 3] Raw detector counts as little-endian int32s. To convert
   *  to millivolts (the unit the vendor displays on the chromatogram
   *  Y axis) multiply by {@link COUNTS_TO_MV} or use {@link signalMv}. */
  Signal: Int32Array;
  /** [Tier 3] Effective sampling rate in Hz used by all per-sample
   *  conversions (StartMin, AreaSeconds). Determined by `parse()`'s
   *  options precedence: explicit `samplingHz` > `useHeaderTickUS` >
   *  {@link DEFAULT_SAMPLING_HZ} (20). See {@link DEFAULT_SAMPLING_HZ}
   *  for the rationale. */
  SamplingHz: number;

  /** [Tier 3] The 104-byte header block, see {@link Header} for
   *  per-field confidence tiers. */
  Header: Header;
  /** [Tier 3] Convenience accessor: `Header.AcquisitionTimestamp`
   *  decoded as a JavaScript Date (Unix epoch seconds × 1000).
   *  `null` only if the timestamp is 0 (older Fi04 demos do not write
   *  this field). Constructed in UTC; format the result with the
   *  consumer's preferred timezone. */
  AcquisitionTime: Date | null;
  /** [Tier 3] First MFC CString after the header. Typically a localized
   *  report-template snippet (header text). May be empty. */
  ReportText1: string;
  /** [Tier 3] Second MFC CString after the header. Typically a
   *  report-template footer. May be empty. */
  ReportText2: string;
  /** [Tier 3] Sample identifier extracted from `ReportText1` by
   *  {@link extractSampleName} — searches for "样品名称：" or
   *  "Sample Name:" and returns the rest of that line. Mirrors HWComp
   *  FUN_004037d0. */
  SampleName: string;

  /** [Tier 3] Calibration / method table; one row per Header.PreCount. */
  Components: Component[];
  /** [Tier 2] U13 mid-block records, between Components and Peaks.
   *  Field semantics unconfirmed — see {@link MidRecord}. */
  MidRecords: MidRecord[];
  /** [Tier 3] Measured peak table; one row per Header.PeakCount. */
  Peaks: Peak[];
  /** [Tier 3] Parsed audit-trail entries. Source is the MFC CString
   *  inside the structured tail (located at `recsEnd + padSize`); see
   *  {@link TailRecords} and {@link decodeStructuredTail} for the
   *  framing. Fi04 / Fi05 write timestamped GBK text here; Fi02 / Fi03
   *  use the same MFC CString slot but the body is opaque binary, so
   *  this array is empty for those versions. Order preserves on-disk
   *  appearance. */
  AuditLog: AuditEntry[];

  /** [Tier 2] `Header.U19` 96-byte structured-tail records that lead
   *  the structured tail. Decoded for **every version** Fi02..Fi05
   *  (verified across the full corpus — same layout in all four). The
   *  *count and framing* are Tier 3; per-record field meaning is
   *  Tier 2 (no HW.exe display oracle). Empty `[]` only on the legacy
   *  magic-less "HW Data File" version, or for any file where the tail
   *  couldn't be cleanly validated. See {@link TailRecord96}. */
  TailRecords: TailRecord96[];
  /** [Tier 2] Fixed pad / scratch block between `TailRecords` and the
   *  audit-log MFC CString. Size is version-dependent — 20 bytes for
   *  Fi02 / Fi03, 28 bytes for Fi04 / Fi05. `null` if the file's
   *  layout couldn't be validated. See {@link TailPadBlock}. */
  TailPad: TailPadBlock | null;
  /** [Tier 1] Trailing little-endian uint32 at end-of-file. Always 0
   *  on Fi02 / Fi03 / Fi04, non-zero only on Fi05 (observed values
   *  ~200..~700). The *position* (last 4 bytes of file) is Tier 3 from
   *  the structured-tail framing; the *meaning* is unknown — could be
   *  a counter, a checksum, a sentinel, or scratch. Named generically
   *  rather than as `EOFSentinel` to avoid implying a function we can't
   *  prove. `null` if the structured tail couldn't be validated. */
  TrailingU32: number | null;

  /** [Tier 3] Bytes left after parsing the structured peak block —
   *  source for {@link AuditLog} on Fi04/Fi05 and a diagnostic dump
   *  otherwise. **Internal/diagnostic.** Prefer iterating
   *  {@link Sections} for layout-driven UIs. */
  TailRemainder: Uint8Array;
  /** [Tier 3] Entire region after the signal trace (header +
   *  everything after). Useful for re-decoding or saving the full
   *  metadata blob unchanged. **Internal/diagnostic.** Prefer iterating
   *  {@link Sections}. */
  RawTail: Uint8Array;

  /** Every byte-range the parser identified, in on-disk order.
   *  Single source of truth for "where in the file does this live?".
   *  Per-section confidence lives on {@link FileSection.Tier} — the
   *  array itself is just the parser's catalogue. The list is **always
   *  exhaustive**: `Σ Sections[i].Length === input.length` holds even
   *  when {@link ParseError} is non-null — `parse()` is fail-soft so
   *  the Layout view can still surface every byte for forensic
   *  inspection. Bytes the parser couldn't claim appear as `unknown`
   *  sections (with `Tier: 1`). */
  Sections: FileSection[];

  /** If the parser hit a fatal condition (no magic, declared sample
   *  count exceeds remaining bytes, truncated header, unknown variant),
   *  this string explains it. Even when set, the rest of the HwFile is
   *  filled with whatever was successfully parsed up to that point and
   *  {@link Sections} still covers 100 % of the input. Parser-runtime
   *  diagnostic — not derived from any file byte, so no tier applies. */
  ParseError: string | null;

  /** Non-fatal parser warnings collected during decode (e.g.
   *  "components table truncated at row 7"). Empty for clean parses.
   *  Parser-runtime diagnostic — no tier applies. */
  ParseWarnings: string[];
}

// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------

/** Options for {@link parse} / {@link parseBlob}. */
export interface ParseOptions {
  /** Override the sampling rate in Hz used for retention-time / area /
   *  height conversions. If omitted, {@link DEFAULT_SAMPLING_HZ} (20) is
   *  used — matching what HWgram / HWComp do when their `HWFrequence.txt`
   *  side-car is at its default. */
  samplingHz?: number;
  /** If true, derive sampling rate from `Header.U6` (`1e6 / U6`)
   *  instead of using the default. **Not recommended** — every production
   *  .hw file in the wild stores `U6 = 200000` (5 Hz) regardless of
   *  the actual capture rate, and the vendor software ignores this field.
   *  Provided only for backward compatibility with prior parser releases.
   *  Ignored when `samplingHz` is also set. */
  useHeaderTickUS?: boolean;
}

/** Parse a .hw file from a byte buffer.
 *
 *  **Fail-soft contract.** This function never throws on malformed
 *  input. On any structural failure (no magic, truncated header,
 *  declared sample count overruns the buffer, unknown version, etc.)
 *  it returns a partially-populated `HwFile` with `ParseError` set and
 *  `Sections` covering 100 % of the input — bytes that couldn't be
 *  claimed by a known phase are emitted as `unknown` sections. This
 *  lets the Layout tab function as a forensic byte-map even on files
 *  the parser doesn't fully understand. */
export function parse(
  input: ArrayBuffer | Uint8Array,
  options: ParseOptions = {},
): HwFile {
  const buf =
    input instanceof Uint8Array ? input : new Uint8Array(input);

  // Skeleton populated up-front so every early-return path returns a
  // structurally valid HwFile. Sections + ParseError are filled in as
  // we go; fillUnknownGaps() at the bottom guarantees full coverage.
  const file: HwFile = {
    Magic: "",
    Version: "",
    MagicAt: 0,
    SampleCount: 0,
    Signal: new Int32Array(0),
    SamplingHz: DEFAULT_SAMPLING_HZ,
    Header: emptyHeader(),
    AcquisitionTime: null,
    ReportText1: "",
    ReportText2: "",
    SampleName: "",
    Components: [],
    MidRecords: [],
    Peaks: [],
    AuditLog: [],
    TailRecords: [],
    TailPad: null,
    TrailingU32: null,
    TailRemainder: new Uint8Array(),
    RawTail: new Uint8Array(),
    Sections: [],
    ParseError: null,
    ParseWarnings: [],
  };

  // Section builder. Emits a {@link FileSection} for the byte-range
  // `[start, end)` and pushes it onto `file.Sections`. Caller is
  // responsible for emitting in strict file-byte order; gaps are
  // filled in at the end by fillUnknownGaps().
  const emit = (
    id: string,
    label: string,
    start: number,
    end: number,
    tier: 1 | 2 | 3,
    note?: string,
  ): void => {
    if (end <= start) return;
    file.Sections.push({
      Id: id,
      Label: label,
      Offset: start,
      Length: end - start,
      Raw: buf.subarray(start, end),
      Tier: tier,
      Note: note,
    });
  };

  // Fail-soft helper: record the error, fill any uncovered bytes as
  // `unknown` sections, return the partially-populated file. All early
  // exits funnel through here so the Layout tab always sees full
  // byte coverage.
  const fail = (err: string): HwFile => {
    file.ParseError = err;
    fillUnknownGaps(file, buf, buf.length);
    return file;
  };

  if (buf.length === 0) {
    return fail("hw: empty input");
  }
  if (buf.length < MAGIC_LEN + 4) {
    // Still emit the bytes we have as a single unknown section.
    return fail(
      `hw: file too short (${buf.length} B; need ≥ ${MAGIC_LEN + 4})`,
    );
  }
  const magic = findMagic(buf);
  if (!magic) {
    return fail(`hw: no HW magic found in ${buf.length} bytes`);
  }
  const { postMagic, version } = magic;
  const magicStart = postMagic - MAGIC_LEN;
  file.Magic = version === "00" ? "HW Data File" : "HW Data Fi" + version;
  file.Version = version;
  file.MagicAt = magicStart;

  if (magicStart > 0) {
    emit(
      "pre-magic",
      "Bytes before magic",
      0,
      magicStart,
      1,
      "Magic scanner mirrors HWgram.exe sub_403740 sliding window; production files start at offset 0",
    );
  }
  emit("magic", `Magic "${file.Magic}"`, magicStart, postMagic, 3);

  if (postMagic + 4 > buf.length) {
    return fail(`hw: truncated header at offset ${postMagic}`);
  }

  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const sampleCount = dv.getUint32(postMagic, true);
  file.SampleCount = sampleCount;
  emit("sample-count", "Sample count (uint32 LE)", postMagic, postMagic + 4, 3);

  let off = postMagic + 4;
  const need = sampleCount * 4;
  if (off + need > buf.length) {
    file.ParseWarnings.push(
      `signal trace truncated: declared ${sampleCount} samples needs ${need} B, only ${buf.length - off} B available`,
    );
    return fail(
      `hw: declared ${sampleCount} samples but only ${buf.length - off} bytes left`,
    );
  }

  // Copy signal into an Int32Array. We prefer a typed-array-view + slice()
  // fast path when the underlying buffer is 4-byte aligned at `off`; this
  // is bit-identical to the per-element loop on little-endian hosts (the
  // .hw format is LE and every browser/Node target we support is also LE).
  const absOff = buf.byteOffset + off;
  let signal: Int32Array;
  if ((absOff & 3) === 0) {
    signal = new Int32Array(buf.buffer, absOff, sampleCount).slice();
  } else {
    signal = new Int32Array(sampleCount);
    for (let i = 0; i < sampleCount; i++) {
      signal[i] = dv.getInt32(off + i * 4, true);
    }
  }
  file.Signal = signal;
  off += need;
  const rawTail = buf.subarray(off);
  file.RawTail = rawTail;
  // Absolute file offset of the first byte of `rawTail` — every Reader
  // position below is converted to a file-absolute offset by adding this.
  const tailBase = off;

  emit("signal", `Signal trace (${sampleCount} × int32 LE)`, postMagic + 4, tailBase, 3);

  const r = new Reader(rawTail);

  // 1. 26 uint32s of header.
  const headerStart = tailBase + r.off;
  if (!parseHeader(r, file.Header)) {
    file.ParseWarnings.push("header truncated");
    emit(
      "header-partial",
      "Header (truncated)",
      headerStart,
      buf.length,
      1,
      "Header expects 26 × uint32 LE; insufficient bytes remain",
    );
    return fail("hw: header truncated");
  }
  emit("header", "Header (26 × uint32 LE)", headerStart, tailBase + r.off, 3);
  if (file.Header.AcquisitionTimestamp > 0) {
    file.AcquisitionTime = new Date(file.Header.AcquisitionTimestamp * 1000);
  }
  if (typeof options.samplingHz === "number" && options.samplingHz > 0) {
    file.SamplingHz = options.samplingHz;
  } else if (options.useHeaderTickUS && file.Header.U6 > 0) {
    file.SamplingHz = 1e6 / file.Header.U6;
  } else {
    file.SamplingHz = DEFAULT_SAMPLING_HZ;
  }

  // 2. Two MFC CStrings (report templates).
  let phaseStart = tailBase + r.off;
  const s1 = r.mfcCString();
  if (!s1) {
    file.ParseWarnings.push("report header CString truncated");
    emit(
      "report-text-1-partial",
      "Report header (truncated MFC CString)",
      phaseStart,
      tailBase + r.off,
      1,
      "MFC CString length prefix/body is incomplete",
    );
    return fail("hw: report header CString truncated");
  }
  if (s1) file.ReportText1 = decodeGBKBest(s1.bytes, s1.unicode);
  emit("report-text-1", "Report header (MFC CString, GBK)", phaseStart, tailBase + r.off, 3);
  phaseStart = tailBase + r.off;
  const s2 = r.mfcCString();
  if (!s2) {
    file.ParseWarnings.push("report footer CString truncated");
    emit(
      "report-text-2-partial",
      "Report footer (truncated MFC CString)",
      phaseStart,
      tailBase + r.off,
      1,
      "MFC CString length prefix/body is incomplete",
    );
    return fail("hw: report footer CString truncated");
  }
  if (s2) file.ReportText2 = decodeGBKBest(s2.bytes, s2.unicode);
  emit("report-text-2", "Report footer (MFC CString, GBK)", phaseStart, tailBase + r.off, 3);
  file.SampleName = extractSampleName(file.ReportText1);

  // 3. Component records.
  const versionCode = parseInt(version, 10);
  const [name1Len, name2Len] = recordWidths(versionCode);
  const recSize = 4 + name1Len + 5 * 4 + name2Len;

  phaseStart = tailBase + r.off;
  for (let i = 0; i < file.Header.PreCount; i++) {
    const raw = r.bytes(recSize);
    if (!raw) break;
    file.Components.push(decodeRecord(i, raw, name1Len, name2Len));
  }
  emit(
    "components",
    `Components (${file.Components.length} × ${recSize} B)`,
    phaseStart,
    tailBase + r.off,
    3,
  );
  if (file.Components.length !== file.Header.PreCount) {
    file.ParseWarnings.push(
      `components table truncated at row ${file.Components.length} of ${file.Header.PreCount}`,
    );
    return fail(
      `hw: components table truncated at row ${file.Components.length} of ${file.Header.PreCount}`,
    );
  }

  // 4. Mid-block records (uint32 + 3 × float32).
  phaseStart = tailBase + r.off;
  for (let i = 0; i < file.Header.MidCount; i++) {
    const raw = r.bytes(16);
    if (!raw) break;
    const v = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
    file.MidRecords.push({
      Word0: v.getUint32(0, true),
      Floats123: [
        v.getFloat32(4, true),
        v.getFloat32(8, true),
        v.getFloat32(12, true),
      ],
    });
  }
  emit(
    "mid-records",
    `MidRecords (${file.MidRecords.length} × 16 B)`,
    phaseStart,
    tailBase + r.off,
    3,
    "HWgram sub_403740 line 2139 confirms 16 B × MidCount; per-field meaning unverified",
  );
  if (file.MidRecords.length !== file.Header.MidCount) {
    file.ParseWarnings.push(
      `mid-records table truncated at row ${file.MidRecords.length} of ${file.Header.MidCount}`,
    );
    return fail(
      `hw: mid-records table truncated at row ${file.MidRecords.length} of ${file.Header.MidCount}`,
    );
  }

  // 5. Peak records.
  phaseStart = tailBase + r.off;
  for (let i = 0; i < file.Header.PeakCount; i++) {
    const raw = r.bytes(recSize);
    if (!raw) break;
    file.Peaks.push(decodeRecord(i, raw, name1Len, name2Len));
  }
  emit(
    "peaks",
    `Peaks (${file.Peaks.length} × ${recSize} B)`,
    phaseStart,
    tailBase + r.off,
    3,
  );
  if (file.Peaks.length !== file.Header.PeakCount) {
    file.ParseWarnings.push(
      `peaks table truncated at row ${file.Peaks.length} of ${file.Header.PeakCount}`,
    );
    return fail(
      `hw: peaks table truncated at row ${file.Peaks.length} of ${file.Header.PeakCount}`,
    );
  }

  file.TailRemainder = r.rest();
  const structuredTailStart = tailBase + r.off;

  // 6. Structured tail (Fi02..Fi05 — same layout, version-dependent
  // pad size). On success this populates TailRecords / TailPad /
  // TrailingU32 and yields an authoritative audit-log byte range.
  const padSize = tailPadSize(versionCode);
  let structuredTailDecoded = false;
  if (padSize !== null) {
    const decoded = decodeStructuredTail(
      file.TailRemainder,
      file.Header.U19,
      padSize,
    );
    if (decoded) {
      structuredTailDecoded = true;
      file.TailRecords = decoded.records;
      file.TailPad = decoded.pad;
      file.TrailingU32 = decoded.trailingU32;
      // Only Fi04+ writes a real timestamped audit log; older versions
      // store opaque binary in the same MFC-CString slot. We still scan
      // for "YYYY/MM/DD HH:MM:SS" markers — Fi02/Fi03 reliably yield 0
      // entries, which is the truthful answer.
      file.AuditLog = parseAuditLog(decoded.auditBytes);

      // Emit structured-tail sections in file-byte order. Offsets are
      // relative to `structuredTailStart` (the byte just after Peaks[]).
      const recsBytes = file.TailRecords.length * 96;
      const padBytes = decoded.pad.Size;
      // `auditOffsetInTail` is the offset of the audit-log payload from
      // the start of the tail buffer; the bytes between
      // `recsBytes + padBytes` and that offset are the MFC CString's
      // length-prefix bytes.
      const auditAbsStart = structuredTailStart + decoded.auditOffsetInTail;
      const auditAbsEnd = auditAbsStart + decoded.auditBytes.length;
      emit(
        "tail-records",
        `Tail records (${file.TailRecords.length} × 96 B)`,
        structuredTailStart,
        structuredTailStart + recsBytes,
        2,
        "Layout verified across full Fi02..Fi05 corpus; per-word semantics unverified",
      );
      emit(
        "tail-pad",
        `Tail pad block (${padBytes} B)`,
        structuredTailStart + recsBytes,
        structuredTailStart + recsBytes + padBytes,
        2,
        "Size pinned per version (20 B Fi02/03, 28 B Fi04/05); content semantics unverified",
      );
      emit(
        "audit-cstring-prefix",
        "Audit-log MFC CString length prefix",
        structuredTailStart + recsBytes + padBytes,
        auditAbsStart,
        3,
      );
      emit(
        "audit-log",
        `Audit log (${file.AuditLog.length} entries, GBK)`,
        auditAbsStart,
        auditAbsEnd,
        3,
      );
      emit(
        "trailing-u32",
        "Trailing uint32 (last 4 bytes of file)",
        auditAbsEnd,
        buf.length,
        1,
        "Position is Tier 3 from tail framing; meaning unknown",
      );
    } else if (version === "04" || version === "05") {
      // Layout didn't validate — fall back to the historical scan over
      // the raw tail so we never lose audit entries on an unexpected
      // variant. (Fi02/Fi03 don't have a text audit log to recover.)
      file.AuditLog = parseAuditLog(file.TailRemainder);
    }
  }
  if (!structuredTailDecoded && structuredTailStart < buf.length) {
    emit(
      "tail-undecoded",
      `Undecoded tail (${buf.length - structuredTailStart} B)`,
      structuredTailStart,
      buf.length,
      1,
      "Structured-tail decoder could not validate framing for this version",
    );
  }

  fillUnknownGaps(file, buf, buf.length);
  return file;
}

/** Walk `file.Sections` (already in file-byte order) and inject
 *  `unknown` sections for any byte ranges not yet claimed — including
 *  pre-first-section bytes and post-last-section bytes. After this
 *  call, `Σ Sections[i].Length === totalLength` is guaranteed. Used
 *  on every parse path (success and fail-soft) so the Layout view
 *  always shows every byte of the input. */
function fillUnknownGaps(
  file: HwFile,
  buf: Uint8Array,
  totalLength: number,
): void {
  // Sort defensively: well-formed parses already emit in order, but
  // fail-soft paths may interleave. Stable sort by Offset.
  file.Sections.sort((a, b) => a.Offset - b.Offset);
  const out: FileSection[] = [];
  let cursor = 0;
  const pushUnknown = (start: number, end: number): void => {
    if (end <= start) return;
    out.push({
      Id: "unknown",
      Label: `Unknown (${end - start} B)`,
      Offset: start,
      Length: end - start,
      Raw: buf.subarray(start, end),
      Tier: 1,
      Note: "Bytes the parser could not assign to a known phase",
    });
  };
  for (const s of file.Sections) {
    if (s.Offset > cursor) pushUnknown(cursor, s.Offset);
    out.push(s);
    cursor = s.Offset + s.Length;
  }
  if (cursor < totalLength) pushUnknown(cursor, totalLength);
  file.Sections = out;
}

/** Parse a File from a browser File / Blob. */
export async function parseBlob(
  blob: Blob,
  options: ParseOptions = {},
): Promise<HwFile> {
  const buf = await blob.arrayBuffer();
  return parse(buf, options);
}

// ---------------------------------------------------------------------------
// Magic scan
// ---------------------------------------------------------------------------

// Pre-encoded magic bytes (hoisted so we don't pay TextEncoder + allocation
// cost on every parse).
const ENCODED_MAGICS: { bytes: Uint8Array; version: string }[] = (() => {
  const enc = new TextEncoder();
  return KNOWN_MAGICS.map((m) => ({
    bytes: enc.encode(m.bytes),
    version: m.version,
  }));
})();

function findMagic(
  b: Uint8Array,
): { postMagic: number; version: string } | null {
  if (b.length < MAGIC_LEN) return null;
  // Sliding one-byte scan, matching HWgram.exe sub_403740 (lines 1896–1941
  // in our IDA dump): a 13-byte rolling buffer is shifted by one byte at a
  // time and compared against each magic. The official loader has no upper
  // bound on the scan; we mirror that to remain output-equivalent.
  for (let off = 0; off + MAGIC_LEN <= b.length; off++) {
    for (const c of ENCODED_MAGICS) {
      let match = true;
      for (let i = 0; i < MAGIC_LEN; i++) {
        if (b[off + i] !== c.bytes[i]) {
          match = false;
          break;
        }
      }
      if (match) {
        return { postMagic: off + MAGIC_LEN, version: c.version };
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Reader (sequential byte cursor)
// ---------------------------------------------------------------------------

interface MfcString {
  bytes: Uint8Array;
  /** True if the on-disk string was UTF-16LE (MFC Unicode marker). */
  unicode: boolean;
}

class Reader {
  private buf: Uint8Array;
  private dv: DataView;
  off = 0;

  constructor(buf: Uint8Array) {
    this.buf = buf;
    this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  bytes(n: number): Uint8Array | null {
    if (this.off + n > this.buf.length) return null;
    const out = this.buf.subarray(this.off, this.off + n);
    this.off += n;
    return out;
  }

  u8(): number | null {
    if (this.off >= this.buf.length) return null;
    return this.buf[this.off++];
  }

  u16(): number | null {
    if (this.off + 2 > this.buf.length) return null;
    const v = this.dv.getUint16(this.off, true);
    this.off += 2;
    return v;
  }

  u32(): number | null {
    if (this.off + 4 > this.buf.length) return null;
    const v = this.dv.getUint32(this.off, true);
    this.off += 4;
    return v;
  }

  rest(): Uint8Array {
    return this.buf.subarray(this.off);
  }

  /**
   * Read an MFC-serialized CString. The format used by
   * CArchive::operator>> for CString is:
   *
   *   if (lenByte = readByte()) < 0xff:                ANSI, length = lenByte
   *   else if (lenU16 = readU16()) < 0xffff:           ANSI, length = lenU16
   *   else:                                            ANSI, length = readU32()
   *
   * With an optional Unicode-marker prefix of bytes {0xff, 0xfe, 0xff}
   * before the length, in which case characters are 16-bit UTF-16LE.
   *
   * An empty string is encoded as a single 0x00 byte.
   */
  mfcCString(): MfcString | null {
    const first = this.u8();
    if (first === null) return null;
    let unicode = false;
    let length = 0;
    if (first === 0xff) {
      // Could be Unicode marker {0xff, 0xfe, 0xff} or a 2-byte length.
      if (
        this.off + 1 < this.buf.length &&
        this.buf[this.off] === 0xfe &&
        this.buf[this.off + 1] === 0xff
      ) {
        this.off += 2;
        unicode = true;
        const lb = this.u8();
        if (lb === null) return null;
        if (lb < 0xff) {
          length = lb;
        } else {
          const w = this.u16();
          if (w === null) return null;
          if (w < 0xffff) {
            length = w;
          } else {
            const l32 = this.u32();
            if (l32 === null) return null;
            length = l32;
          }
        }
      } else {
        const w = this.u16();
        if (w === null) return null;
        if (w < 0xffff) {
          length = w;
        } else {
          const l32 = this.u32();
          if (l32 === null) return null;
          length = l32;
        }
      }
    } else {
      length = first;
    }
    let byteCount = length;
    if (unicode) byteCount *= 2;
    const out = this.bytes(byteCount);
    if (!out) return null;
    return { bytes: out, unicode };
  }
}

// ---------------------------------------------------------------------------
// Header & records
// ---------------------------------------------------------------------------

function emptyHeader(): Header {
  return {
    U0: 0, U1: 0, U2: 0, U3: 0, U4: 0, U5: 0,
    U6: 0,
    U7: 0, U8: 0, U9: 0, U10: 0,
    PreCount: 0, PeakCount: 0, MidCount: 0,
    U14: 0, AcquisitionTimestamp: 0, U16: 0,
    U17: 0, U18: 0,
    U19: 0, U20: 0, U21: 0,
    U22: 0, Amount: 0, U24: 0, Dilution: 0,
  };
}

function parseHeader(r: Reader, h: Header): boolean {
  const raw = r.bytes(HEADER_SIZE);
  if (!raw) return false;
  const v = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  const u = (i: number) => v.getUint32(i * 4, true);
  const fl = (i: number) => v.getFloat32(i * 4, true);
  h.U0 = u(0);
  h.U1 = u(1);
  h.U2 = u(2);
  h.U3 = u(3);
  h.U4 = u(4);
  h.U5 = u(5);
  h.U6 = u(6);
  h.U7 = u(7);
  h.U8 = u(8);
  h.U9 = u(9);
  h.U10 = u(10);
  h.PreCount = u(11);
  h.PeakCount = u(12);
  h.MidCount = u(13);
  h.U14 = u(14);
  h.AcquisitionTimestamp = u(15);
  h.U16 = u(16);
  h.U17 = u(17);
  h.U18 = u(18);
  h.U19 = u(19);
  h.U20 = u(20);
  h.U21 = u(21);
  h.U22 = fl(22);
  h.Amount = fl(23);
  h.U24 = fl(24);
  h.Dilution = fl(25);
  return true;
}

/**
 * Returns the version-dependent (name1Len, name2Len) per HWComp.exe at
 * 0x40307a / 0x40341e. Branches:
 *
 *   name1Len = 17 if version==0 else 33
 *   name2Len =  7 if version<=2 else 33
 */
function recordWidths(versionCode: number): [number, number] {
  const n1 = versionCode === 0 ? 17 : 33;
  const n2 = versionCode <= 2 ? 7 : 33;
  return [n1, n2];
}

/**
 * Interprets the 4 + N1 + 20 + N2 bytes of a Component or Peak record
 * into a {@link PeakRow}.
 *
 * Field meaning of the 5 g-slots was cross-checked against HWComp.exe's
 * report-table renderer (sub_405210, switch cases 6..11). The disassembly
 * shows that slot 3 is INT32 (peak height in counts), not float32 as the
 * prior Go port assumed; everything else is float32. We deliberately
 * keep the parser dumb about derived values — the named accessors
 * ({@link peakRetentionMin}, {@link peakAreaCounts}, etc.) own every
 * formula HW.exe applies.
 */
function decodeRecord(
  index: number,
  raw: Uint8Array,
  n1: number,
  n2: number,
): PeakRow {
  const dv = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  const startSamples = dv.getFloat32(0, true);
  const name = trimAndDecodeGBK(raw.subarray(4, 4 + n1));
  const gbase = 4 + n1;
  const g: [number, number, number, number, number] = [
    dv.getFloat32(gbase, true),
    dv.getFloat32(gbase + 4, true),
    dv.getFloat32(gbase + 8, true),
    // Slot 3 is INT32 on disk (peak height in detector counts).
    // peakHeightCounts() is the SSOT accessor.
    dv.getInt32(gbase + 12, true),
    dv.getFloat32(gbase + 16, true),
  ];
  const flag = trimAndDecodeGBK(raw.subarray(gbase + 20, gbase + 20 + n2));
  return {
    Index: index,
    Name: name,
    Flag: flag,
    StartSamples: startSamples,
    G: g,
    Raw: new Uint8Array(raw),
  };
}

// ---------------------------------------------------------------------------
// Fi05 structured tail
// ---------------------------------------------------------------------------

/** Decoded structured tail — see {@link decodeStructuredTail}. */
interface DecodedTail {
  records: TailRecord96[];
  pad: TailPadBlock;
  /** Byte slice covering only the audit-log payload (no length prefix,
   *  no trailing uint32). Safe to pass straight to `parseAuditLog`. */
  auditBytes: Uint8Array;
  /** Offset of `auditBytes` from the start of the tail buffer (i.e.
   *  from the byte just after `Peaks[]`). Equals
   *  `records.length * 96 + pad.Size + cstrPrefixLen`. */
  auditOffsetInTail: number;
  trailingU32: number;
}

/** Read the length of an MFC `CString` serialised by `CArchive::operator<<`.
 *
 *  Encoding (matches the disassembly of `CArchive::WriteString` / the MFC
 *  source): a single byte length, escaping into 3 / 7 byte wide forms when
 *  the string is too long. We support up to the uint16 form (4-byte uint32
 *  length is also handled for completeness even though the audit log
 *  never gets near 64 KiB).
 *
 *  Returns `{ length, headerLen }` or `null` if the prefix is malformed. */
function readMfcCStringLen(
  buf: Uint8Array,
  off: number,
): { length: number; headerLen: number } | null {
  if (off >= buf.length) return null;
  const first = buf[off];
  // Short form: single byte length 0..254.
  if (first < 0xff) return { length: first, headerLen: 1 };
  // Escape: 0xff followed by uint16 length, escaping further to uint32 if
  // the uint16 itself is 0xffff (Unicode marker handled separately by MFC
  // — none of our files use it, so we reject for safety).
  if (off + 2 >= buf.length) return null;
  if (buf[off + 1] === 0xfe && buf[off + 2] === 0xff) return null;
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const len16 = dv.getUint16(off + 1, true);
  if (len16 < 0xffff) return { length: len16, headerLen: 3 };
  if (off + 6 >= buf.length) return null;
  const len32 = dv.getUint32(off + 3, true);
  return { length: len32, headerLen: 7 };
}

const TAIL_RECORD_SIZE = 96;
const TRAILING_U32_SIZE = 4;

/** Pad-block size between TailRecords[] and the audit-log MFC
 *  CString, keyed by file version. Verified empirically on the full
 *  14-file corpus (`re/probe_layout_all.ts`):
 *
 *    Fi02 / Fi03 → 20 bytes
 *    Fi04 / Fi05 → 28 bytes
 *
 *  Older "HW Data File" / Fi01 files don't have a structured tail at
 *  all (no examples in our corpus). */
function tailPadSize(versionCode: number): number | null {
  if (versionCode === 2 || versionCode === 3) return 20;
  if (versionCode === 4 || versionCode === 5) return 28;
  return null;
}

/** Decode the version-dependent structured tail.
 *
 *  Universal layout (validated across the full 14-file corpus):
 *    [u19RecCount × 96 bytes]   — TailRecord96[]
 *    [padSize bytes]            — TailPadBlock (20 for Fi02/03, 28 for Fi04/05)
 *    [MFC CString prefix + body]— audit-log payload
 *    [4 bytes]                  — trailing uint32 (HwFile.TrailingU32)
 *
 *  The audit-string body is *human-readable GBK timestamps* on Fi04/Fi05
 *  ("YYYY/MM/DD HH:MM:SS  …") but appears to be opaque binary on
 *  Fi02/Fi03 — the framing is identical, the encoding differs.
 *
 *  Returns `null` (and the caller falls back to a heuristic scan) if
 *  any size check fails — we never throw, so a freshly-saved variant
 *  of the format won't break callers. */
function decodeStructuredTail(
  tail: Uint8Array,
  u19RecCount: number,
  padSize: number,
): DecodedTail | null {
  if (u19RecCount < 0) return null;
  const recsBytes = u19RecCount * TAIL_RECORD_SIZE;
  // We need at least: records + pad + 1-byte CString prefix + 4-byte trailing u32.
  const minSize = recsBytes + padSize + 1 + TRAILING_U32_SIZE;
  if (tail.length < minSize) return null;

  // 1. Structured-tail records.
  const records: TailRecord96[] = new Array(u19RecCount);
  for (let i = 0; i < u19RecCount; i++) {
    const o = i * TAIL_RECORD_SIZE;
    const raw = tail.subarray(o, o + TAIL_RECORD_SIZE);
    // Copy into a freshly-allocated Uint32Array so the buffer is
    // 4-aligned regardless of `tail.byteOffset` (Uint32Array
    // construction over an unaligned Uint8Array buffer throws).
    const words = new Uint32Array(24);
    const dvw = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
    for (let w = 0; w < 24; w++) words[w] = dvw.getUint32(w * 4, true);
    const w11Float = dvw.getFloat32(11 * 4, true);
    records[i] = {
      Index: i,
      Word0: words[0],
      Word1: words[1],
      Word10: words[10],
      Word11AsFloat: Number.isFinite(w11Float) ? w11Float : 0,
      Words: words,
      Raw: raw,
    };
  }

  // 2. Pad block — decode as little-endian uint32 words for inspection.
  const padRaw = tail.subarray(recsBytes, recsBytes + padSize);
  const dvs = new DataView(padRaw.buffer, padRaw.byteOffset, padRaw.byteLength);
  const padWords: number[] = [];
  for (let i = 0; i + 4 <= padSize; i += 4)
    padWords.push(dvs.getUint32(i, true));
  const pad: TailPadBlock = {
    Size: padSize,
    Words: padWords,
    Raw: padRaw,
  };

  // 3. MFC CString audit log.
  const cstrOff = recsBytes + padSize;
  const cstr = readMfcCStringLen(tail, cstrOff);
  if (!cstr) return null;
  const auditStart = cstrOff + cstr.headerLen;
  const auditEnd = auditStart + cstr.length;
  // Must leave exactly the 4-byte trailing uint32 at the end.
  if (auditEnd + TRAILING_U32_SIZE !== tail.length) return null;
  const auditBytes = tail.subarray(auditStart, auditEnd);

  // 4. Trailing uint32 (HwFile.TrailingU32).
  const dvEof = new DataView(
    tail.buffer,
    tail.byteOffset + auditEnd,
    TRAILING_U32_SIZE,
  );
  const trailingU32 = dvEof.getUint32(0, true);

  return { records, pad, auditBytes, auditOffsetInTail: auditStart, trailingU32 };
}

// ---------------------------------------------------------------------------
// Audit log + sample-name extraction
// ---------------------------------------------------------------------------

const AUDIT_TS_RE =
  /(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})/g;

function parseAuditLog(tail: Uint8Array): AuditEntry[] {
  // The audit log is GBK text; timestamps are pure ASCII so we can search
  // directly on the bytes via a UTF-8-ish view (every byte we look for is
  // <0x80 so byte-wise indexing works regardless of GBK).
  const ascii = new TextDecoder("latin1").decode(tail);
  const matches = [...ascii.matchAll(AUDIT_TS_RE)];
  if (matches.length === 0) return [];
  const out: AuditEntry[] = [];
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const start = m.index!;
    const tsEnd = start + m[0].length;
    const t = new Date(
      parseInt(m[1], 10),
      parseInt(m[2], 10) - 1,
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      parseInt(m[6], 10),
    );
    if (Number.isNaN(t.getTime())) continue;
    let msgStart = tsEnd;
    if (
      msgStart < tail.length &&
      (tail[msgStart] === 0x20 || tail[msgStart] === 0x09)
    ) {
      msgStart++;
    }
    if (msgStart < tail.length && tail[msgStart] === 0x09) {
      msgStart++;
    }
    const msgEndCap =
      i + 1 < matches.length ? matches[i + 1].index! : tail.length;
    let end = msgEndCap;
    for (let j = msgStart; j < msgEndCap; j++) {
      const c = tail[j];
      if (c === 0 || c === 0x0a || c === 0x0d) {
        end = j;
        break;
      }
    }
    const msg = decodeGBKBest(tail.subarray(msgStart, end), false).trim();
    out.push({ Time: t, Message: msg });
  }
  return out;
}

/**
 * Mirrors FUN_004037d0: search ReportText1 for either "样品名称：" (Chinese)
 * or "Sample Name:" (English) and return the remainder of that line, stopping
 * at CR/LF.
 */
function extractSampleName(report: string): string {
  if (!report) return "";
  const keys = ["样品名称：", "Sample Name:", "Sample name:"];
  for (const k of keys) {
    const i = report.indexOf(k);
    if (i < 0) continue;
    let rest = report.substring(i + k.length);
    const j = rest.search(/[\r\n]/);
    if (j >= 0) rest = rest.substring(0, j);
    return rest.trim();
  }
  return "";
}

// ---------------------------------------------------------------------------
// String decoding
// ---------------------------------------------------------------------------

function trimAndDecodeGBK(b: Uint8Array): string {
  // Truncate at first NUL.
  let end = b.length;
  for (let i = 0; i < b.length; i++) {
    if (b[i] === 0) {
      end = i;
      break;
    }
  }
  // Trim trailing spaces.
  while (end > 0 && b[end - 1] === 0x20) end--;
  if (end === 0) return "";
  return decodeGBKBest(b.subarray(0, end), false).trim();
}

// Cache decoders so we don't pay construction cost per record.
let gbkDecoder: TextDecoder | null = null;
let utf16leDecoder: TextDecoder | null = null;
let latin1Decoder: TextDecoder | null = null;

function getGbkDecoder(): TextDecoder {
  if (gbkDecoder) return gbkDecoder;
  // GB18030 is a superset of GBK and is mandatory in WHATWG TextDecoder.
  gbkDecoder = new TextDecoder("gb18030", { fatal: false });
  return gbkDecoder;
}

function getUtf16Decoder(): TextDecoder {
  if (utf16leDecoder) return utf16leDecoder;
  utf16leDecoder = new TextDecoder("utf-16le", { fatal: false });
  return utf16leDecoder;
}

function getLatin1Decoder(): TextDecoder {
  if (latin1Decoder) return latin1Decoder;
  latin1Decoder = new TextDecoder("latin1");
  return latin1Decoder;
}

function decodeGBKBest(b: Uint8Array, unicode: boolean): string {
  if (b.length === 0) return "";
  if (unicode) {
    try {
      return getUtf16Decoder().decode(b);
    } catch {
      return getLatin1Decoder().decode(b);
    }
  }
  try {
    return getGbkDecoder().decode(b);
  } catch {
    return getLatin1Decoder().decode(b);
  }
}

// ---------------------------------------------------------------------------
// Derived-value accessors and HW.exe-faithful display formatters
// ---------------------------------------------------------------------------
//
// This section is the single source of truth for every per-row quantity
// HW.exe shows. The on-disk record (`PeakRow`) carries only the bytes;
// every formula HW.exe applies — and every numeric format string the
// vendor uses to print the result — lives below. Consumers (UI, tests,
// `re/` probes) call these accessors and constants and never re-derive.
//
// Each accessor / format constant carries:
//   - the disassembly reference (HWComp/HWgram subroutine + line)
//   - the empirical cross-check (which `re/` probe / verify script
//     proves it matches HW.exe's actual output)
//
// Promotion to Tier 3 requires both.

/** [Tier 3] Conversion factor from raw detector counts to millivolts.
 *  Disassembly: HWgram.exe sub_402650 multiplies by 0.001 before
 *  plotting on the "mV" Y axis (lines 968–972, 1005 in the IDA dump).
 *  Cross-check: chromatograms render pixel-aligned to HWgram on every
 *  fixture in the corpus. HWgram does NOT apply any per-file Header
 *  float here — those header dwords are read off the wire and discarded
 *  by both viewers. */
export const COUNTS_TO_MV = 0.001;

/** Signal trace converted to millivolts. */
export function signalMv(file: HwFile): Float32Array {
  const out = new Float32Array(file.Signal.length);
  for (let i = 0; i < out.length; i++) out[i] = file.Signal[i] * COUNTS_TO_MV;
  return out;
}

// --- per-peak HW.exe-equivalent accessors ----------------------------------
//
// `hz` is **always** taken from `HwFile.SamplingHz` (which itself
// resolves the ParseOptions precedence). Don't accept a raw `samplingHz`
// argument — that would let callers pass an inconsistent rate without
// knowing it.

/** [Tier 3] Retention time in **minutes** — HW.exe column "保留时间".
 *  Formula `StartSamples / (SamplingHz × 60)`.
 *  Disassembly: HWComp sub_405210 case 6 line 4239
 *    `sprintf(Buffer, "%.3f", *(float*)v29 / ((double)n20 * 60.0))`.
 *  Cross-check: ≤0.001 min difference vs RTF "保留时间" column on every
 *  peak in every non-stale real-world fixture (`re/verify_real_world.ts`). */
export function peakRetentionMin(p: PeakRow, hz: number): number {
  return hz > 0 ? p.StartSamples / (hz * 60) : 0;
}

/** [Tier 3] Peak area in detector-count seconds — HW.exe column "峰面积".
 *  Formula `G[2] / SamplingHz`.
 *  Disassembly: HWComp sub_405210 case 9 line 4262
 *    `sprintf(Buffer, "%.0f", *((float*)v29 + 12) / (double)n20)`.
 *  Cross-check: ≤0.04 % deviation vs RTF "峰面积" column on every peak
 *  in every non-stale real-world fixture; total area ≤0.05 %
 *  (`re/verify_real_world.ts`). */
export function peakAreaCounts(p: PeakRow, hz: number): number {
  return hz > 0 ? p.G[2] / hz : 0;
}

/** [Tier 3] Peak height in raw detector counts — HW.exe column "峰高".
 *  Formula: read G slot 3 as int32 (NOT float32 — see {@link PeakRow.G}).
 *  Disassembly: HWComp sub_405210 case 10 line 4264
 *    `Value = *((_DWORD*)v29 + 13); ltoa(Value, Buffer, 10)`.
 *  Cross-check: pinned values 215808 / 509234 (baijiu peaks 0/1) and
 *  1121 / 11739 / 9402 (white-H peaks 0/1/2) — both verified against
 *  HW.exe's data view. To convert to mV: `peakHeightCounts(p) * COUNTS_TO_MV`. */
export function peakHeightCounts(p: PeakRow): number {
  return p.G[3] | 0;
}

/** [Tier 3] Peak FWHM (full width at half maximum) in seconds —
 *  HW.exe column "半高峰宽". Formula `G[4] / SamplingHz`.
 *  Disassembly: HWComp sub_405210 case 11 line 4266
 *    `sprintf(Buffer, "%.3f", *((float*)v29 + 14) / (double)n20)`.
 *  Cross-check: the Gaussian-area identity
 *      AreaCounts = HeightCounts × FWHM × √(π/(4·ln 2))
 *  holds to four decimal places (ratio = 1.000 ± 5e-4) for every peak
 *  in white-H — see `re/probe_width_fwhm.ts`. The width slot really is
 *  FWHM and not some other width metric; HW.exe's stored area was
 *  itself computed from the Gaussian model. */
export function peakFwhmSec(p: PeakRow, hz: number): number {
  return hz > 0 ? p.G[4] / hz : 0;
}

/** [Tier 3] Per-peak area-percent — HW.exe column "峰面积%" in the
 *  exported RTF/PNG report and "Conc" in the on-screen data view.
 *  Reads G slot 0 verbatim: HW.exe pre-computes this at save time, so
 *  there is no runtime formula. Empirically verified across every
 *  uncalibrated real-world Fi05 fixture: G[0] = (AreaCounts / Σ
 *  AreaCounts) × 100 to float32 precision (`re/probe_rf_column.ts`,
 *  max deviation 4e-4 percentage points). On *calibrated* runs (e.g.
 *  demo_baijiu_Fi05.hw) HW.exe overwrites the slot with a
 *  calibration-scaled reading — same column header, different formula
 *  inside HW.exe; we surface the slot as-is either way. */
export function peakAreaPercent(p: PeakRow): number {
  return p.G[0];
}

// --- HW.exe-faithful display formatters ------------------------------------
//
// These are the literal printf format strings HW.exe applies to each
// quantity. Centralized here so the UI can't drift (and a regression
// like the recent `%.3g` Area drift can't happen again).

/** HW.exe's "保留时间" column uses `%.3f` (HWComp case 6 line 4239). */
export function fmtRetentionMin(min: number): string {
  return min.toFixed(3);
}

/** HW.exe's "峰面积" column uses `%.0f` (HWComp case 9 line 4262).
 *  Returns the integer count value as a string. */
export function fmtAreaCounts(area: number): string {
  return Math.round(area).toString();
}

/** HW.exe's "峰高" column prints the int32 with `ltoa(...,10)`
 *  (HWComp case 10 line 4264). */
export function fmtHeightCounts(h: number): string {
  return Math.trunc(h).toString();
}

/** HW.exe's "半高峰宽" column uses `%.3f` (HWComp case 11 line 4266). */
export function fmtFwhmSec(sec: number): string {
  return sec.toFixed(3);
}

/** HW.exe's area-percent column uses `%-7.4g` (HWComp case 7
 *  rendering — `%-7.4g` means left-aligned, 4 significant figures).
 *  We approximate with JS's `toPrecision(4)` then strip trailing zeros. */
export function fmtAreaPercent(pct: number): string {
  if (!Number.isFinite(pct)) return String(pct);
  if (pct === 0) return "0";
  return Number.parseFloat(pct.toPrecision(4)).toString();
}

// ---------------------------------------------------------------------------
// Summary / debug
// ---------------------------------------------------------------------------

/** Multi-line human-readable summary, useful for CLI / debugging. */
export function info(f: HwFile): string {
  const lines: string[] = [];
  lines.push(`Magic           : ${f.Magic} (version ${f.Version})`);
  if (f.MagicAt !== 0) lines.push(`Magic offset    : ${f.MagicAt}`);
  lines.push(`Sample count    : ${f.SampleCount}`);
  lines.push(`Sampling rate   : ${f.SamplingHz.toFixed(3)} Hz`);
  if (f.Signal.length > 0) {
    let mn = f.Signal[0], mx = f.Signal[0];
    for (let i = 1; i < f.Signal.length; i++) {
      const v = f.Signal[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    lines.push(
      `Signal range    : ${mn} .. ${mx} counts  (${(mn * COUNTS_TO_MV).toFixed(3)} .. ${(mx * COUNTS_TO_MV).toFixed(3)} mV)`,
    );
  }
  const h = f.Header;
  lines.push("Header          :");
  lines.push(
    `  U6 (TickUS?)  : ${h.U6}  (ignored; actual ${f.SamplingHz.toFixed(3)} Hz)`,
  );
  lines.push(`  Components    : ${h.PreCount}`);
  lines.push(`  Peaks         : ${h.PeakCount}`);
  lines.push(`  MidRecords    : ${h.MidCount}`);
  lines.push(`  U17           : ${h.U17}`);
  lines.push(`  U18           : ${h.U18}`);
  lines.push(
    `  Amount/Dilu   : ${h.Amount} / ${h.Dilution}  (U22=${h.U22} U24=${h.U24})`,
  );
  if (f.AcquisitionTime) {
    lines.push(`Acquisition     : ${f.AcquisitionTime.toISOString()}`);
  }
  if (f.SampleName) lines.push(`Sample name     : ${f.SampleName}`);
  lines.push(`Components      : ${f.Components.length} decoded`);
  lines.push(`Peaks           : ${f.Peaks.length} decoded`);
  lines.push(`MidRecords      : ${f.MidRecords.length} decoded`);
  lines.push(
    `TailRecords     : ${f.TailRecords.length} decoded` +
      (f.TailPad ? `  (+${f.TailPad.Size}-byte pad block)` : "") +
      (f.TrailingU32 !== null ? `  TrailingU32=${f.TrailingU32}` : ""),
  );
  lines.push(`Audit entries   : ${f.AuditLog.length}`);
  lines.push(
    `Tail bytes      : ${f.RawTail.length} (${f.TailRemainder.length} remaining after structured parse)`,
  );
  if (f.AuditLog.length > 0) {
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours(),
      )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    lines.push(
      `First audit     : ${fmt(f.AuditLog[0].Time)}  ${f.AuditLog[0].Message}`,
    );
    const last = f.AuditLog[f.AuditLog.length - 1];
    lines.push(`Last  audit     : ${fmt(last.Time)}  ${last.Message}`);
  }
  return lines.join("\n");
}

function pad(n: number): string {
  return n < 10 ? "0" + n : "" + n;
}
